import { QueryClient } from '@tanstack/react-query';
import { UNIVERSAL_CACHE_KEYS, getCacheKeysForContentType } from './cache-keys';

/**
 * Comprehensive cache update utilities for Universal Storage
 * Handles surgical updates across all cache layers
 */

interface CacheUpdateParams {
  queryClient: QueryClient;
  itemId: string;
  contentType: string;
  categoryId: string | null;
  triggerRefetch: () => void;
}

/**
 * Update the main storage items cache
 */
const updateStorageItemsCache = (
  queryClient: QueryClient,
  itemId: string,
  contentType: string,
  categoryId: string | null
) => {
  // Update main storage items cache
  queryClient.setQueryData(UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS, (oldData: any) => {
    if (!oldData) return oldData;
    
    const existingItemIndex = oldData.findIndex((item: any) => {
      // Check the appropriate foreign key based on content type
      if (contentType === 'private_image' && item.workbook_image_id === itemId) return true;
      if (contentType === 'private_research' && item.workbook_research_id === itemId) return true;
      if (contentType === 'private_page' && item.workbook_page_id === itemId) return true;
      return false;
    });
    
    if (categoryId === null) {
      // Remove item from storage
      return oldData.filter((item: any) => {
        // Check the appropriate foreign key based on content type
        if (contentType === 'private_image' && item.workbook_image_id === itemId) return false;
        if (contentType === 'private_research' && item.workbook_research_id === itemId) return false;
        if (contentType === 'private_page' && item.workbook_page_id === itemId) return false;
        return true;
      });
    } else if (existingItemIndex >= 0) {
      // Update existing item's category
      return oldData.map((item: any, index: number) => 
        index === existingItemIndex ? { ...item, category_id: categoryId } : item
      );
    } else {
      // Add new item to storage (optimistic - will be properly updated on next refetch)
      return oldData;
    }
  });

  // Update filtered storage caches
  queryClient.invalidateQueries({ 
    queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS, 
    exact: false 
  });
};

/**
 * Update Universal Library data caches
 */
const updateUniversalLibraryCaches = (
  queryClient: QueryClient,
  itemId: string,
  contentType: string,
  categoryId: string | null,
  triggerRefetch: () => void
) => {
  // Update Universal Library data cache
  queryClient.setQueryData(UNIVERSAL_CACHE_KEYS.LIBRARY_DATA, (oldData: any) => {
    if (!oldData || !Array.isArray(oldData)) return oldData;
    
    if (categoryId === null) {
      // Remove item from library view
      return oldData.filter((item: any) => 
        !(item.id === itemId && item.contentType === contentType)
      );
    } else {
      // Update existing item or keep as-is (will be updated by refetch)
      return oldData.map((item: any) => {
        if (item.id === itemId && item.contentType === contentType) {
          return {
            ...item,
            originalItem: {
              ...item.originalItem,
              category_id: categoryId
            }
          };
        }
        return item;
      });
    }
  });

  // Trigger selective refetch for Universal Library components
  triggerRefetch();
};

/**
 * Update generator-specific caches
 */
const updateGeneratorCaches = (
  queryClient: QueryClient,
  itemId: string,
  contentType: string,
  categoryId: string | null
) => {
  switch (contentType) {
    case 'image':
      // Invalidate image library caches with pattern matching
      queryClient.invalidateQueries({ 
        queryKey: UNIVERSAL_CACHE_KEYS.IMAGE_LIBRARY,
        exact: false 
      });
      break;
      
    case 'research':
      // Invalidate research library caches
      queryClient.invalidateQueries({ 
        queryKey: UNIVERSAL_CACHE_KEYS.RESEARCH_LIBRARY_LIST,
        exact: false 
      });
      
      // Also invalidate specific research item details
      queryClient.invalidateQueries({ 
        queryKey: UNIVERSAL_CACHE_KEYS.RESEARCH_ITEM_DETAILS(itemId),
        exact: false 
      });
      break;
      
    case 'workbook':
      // Invalidate workbook library caches
      queryClient.invalidateQueries({ 
        queryKey: UNIVERSAL_CACHE_KEYS.WORKBOOK_LIBRARY,
        exact: false 
      });
      break;
  }
};

/**
 * Update category count caches
 */
const updateCategoryCounts = (queryClient: QueryClient) => {
  // Invalidate category counts - these are complex to update surgically
  queryClient.invalidateQueries({ 
    queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORY_COUNTS,
    exact: false 
  });
  
  queryClient.invalidateQueries({ 
    queryKey: UNIVERSAL_CACHE_KEYS.LIBRARY_COUNTS,
    exact: false 
  });
};

/**
 * Main comprehensive cache update function
 * This replaces the original updateCachesSurgically with full coverage
 */
export const updateAllCachesSurgically = ({
  queryClient,
  itemId,
  contentType,
  categoryId,
  triggerRefetch,
}: CacheUpdateParams) => {
  console.log('[CacheUpdate] Starting comprehensive cache update:', {
    itemId,
    contentType,
    categoryId,
    timestamp: new Date().toISOString()
  });

  try {
    // 1. Update specific item category cache
    queryClient.setQueryData(
      UNIVERSAL_CACHE_KEYS.ITEM_CATEGORY(itemId, contentType), 
      categoryId
    );

    // 2. Update main storage items cache
    updateStorageItemsCache(queryClient, itemId, contentType, categoryId);

    // 3. Update Universal Library caches
    updateUniversalLibraryCaches(queryClient, itemId, contentType, categoryId, triggerRefetch);

    // 4. Update generator-specific caches
    updateGeneratorCaches(queryClient, itemId, contentType, categoryId);

    // 5. Update category counts
    updateCategoryCounts(queryClient);

    // 6. Enhanced fallback: If no refetch callbacks are registered, 
    // ensure React Query caches are invalidated for immediate UI updates
    setTimeout(() => {
      // Small delay to allow triggerRefetch to complete
      const cacheKeys = getCacheKeysForContentType(contentType);
      cacheKeys.forEach(cacheKey => {
        queryClient.invalidateQueries({ queryKey: cacheKey, exact: false });
      });
      console.log('[CacheUpdate] Enhanced fallback invalidation completed for', contentType);
    }, 100);

    console.log('[CacheUpdate] Comprehensive cache update completed successfully');
  } catch (error) {
    console.error('[CacheUpdate] Error during cache update:', error);
    
    // Fallback: trigger full refetch if surgical updates fail
    console.warn('[CacheUpdate] Falling back to full refetch due to error');
    triggerRefetch();
    
    // Also invalidate all related caches as fallback
    const cacheKeys = getCacheKeysForContentType(contentType);
    cacheKeys.forEach(cacheKey => {
      queryClient.invalidateQueries({ queryKey: cacheKey, exact: false });
    });
  }
};

/**
 * Utility to invalidate all caches for a content type (emergency fallback)
 */
export const invalidateAllCachesForContentType = (
  queryClient: QueryClient,
  contentType: string
) => {
  console.warn('[CacheUpdate] Emergency cache invalidation for content type:', contentType);
  
  const cacheKeys = getCacheKeysForContentType(contentType);
  cacheKeys.forEach(cacheKey => {
    queryClient.invalidateQueries({ queryKey: cacheKey, exact: false });
  });
  
  // Also invalidate counts
  updateCategoryCounts(queryClient);
}; 